import {SagaIterator} from "redux-saga";
import {call, put, select} from "typed-redux-saga";
import {Api, ApiError} from "modules/utils/Api";
import {
	fetchAllLeaderboardsData,
	fetchAllLeaderboardsDataFailed,
	fetchAllTimeLeaderboard,
	fetchDailyLeaderboard,
	fetchLeaderboardSuccess,
	fetchLeaderProfile,
	fetchLeaderProfileFailed,
	fetchLeaderProfileSuccess,
	fetchYesterdayLeaderboard,
	ILeaderboardResponse,
	showErrorModal,
} from "modules/actions";
import {getActiveContest} from "modules/selectors";
import {getCurrentLeaderboard, getSelectedLeaderboard} from "modules/selectors/leaderboards";
import {LeaderboardsType} from "modules/enums";
import {isNull} from "lodash";

export const fetchYesterdayLeaderboardSaga = function* ({
	payload,
}: ReturnType<typeof fetchYesterdayLeaderboard>): SagaIterator {
	try {
		const storeRanks = yield* select(getCurrentLeaderboard);
		const {haveToClear, isPrevLeaderboard} = payload;

		if (isNull(storeRanks)) return;

		const response = yield* call(Api.Ranking.getYesterdayLeaderboard, payload);

		ApiError.CHECK(response.data);

		const result: ILeaderboardResponse = {
			...response.data.success,
			ranks: haveToClear
				? response.data.success.ranks
				: isPrevLeaderboard
				? [...response.data.success.ranks, ...storeRanks.ranks]
				: [...storeRanks.ranks, ...response.data.success.ranks],

			nextPage: isPrevLeaderboard ? storeRanks.nextPage : response.data.success.nextPage,
		};

		yield* put(fetchLeaderboardSuccess(result));
	} catch (e) {
		const error = e as ApiError;
		yield* put(showErrorModal(error.message));
	}
};

export const fetchDailyLeaderboardSaga = function* ({
	payload,
}: ReturnType<typeof fetchDailyLeaderboard>): SagaIterator {
	try {
		const activeContest = yield* select(getActiveContest);
		const storeRanks = yield* select(getCurrentLeaderboard);
		const {haveToClear, isPrevLeaderboard} = payload;

		if (isNull(storeRanks)) return;

		if (!activeContest) {
			return;
		}

		const response = yield* call(Api.Ranking.getDailyLeaderboard, {
			...payload,
			contestId: activeContest.id,
		});

		ApiError.CHECK(response.data);

		const result: ILeaderboardResponse = {
			...response.data.success,
			ranks: haveToClear
				? response.data.success.ranks
				: isPrevLeaderboard
				? [...response.data.success.ranks, ...storeRanks.ranks]
				: [...storeRanks.ranks, ...response.data.success.ranks],

			nextPage: isPrevLeaderboard ? storeRanks.nextPage : response.data.success.nextPage,
		};

		yield* put(fetchLeaderboardSuccess(result));
	} catch (e) {
		const error = e as ApiError;
		yield* put(showErrorModal(error.message));
	}
};

export const fetchAllTimeLeaderboardSaga = function* ({
	payload,
}: ReturnType<typeof fetchAllTimeLeaderboard>): SagaIterator {
	try {
		const storeRanks = yield* select(getCurrentLeaderboard);
		const {haveToClear, isPrevLeaderboard} = payload;

		if (isNull(storeRanks)) return;

		const response = yield* call(Api.Ranking.getAllTimeLeaderboard, payload);

		ApiError.CHECK(response.data);

		const result: ILeaderboardResponse = {
			...response.data.success,
			ranks: haveToClear
				? response.data.success.ranks
				: isPrevLeaderboard
				? [...response.data.success.ranks, ...storeRanks.ranks]
				: [...storeRanks.ranks, ...response.data.success.ranks],

			nextPage: isPrevLeaderboard ? storeRanks.nextPage : response.data.success.nextPage,
		};

		yield* put(fetchLeaderboardSuccess(result));
	} catch (e) {
		const error = e as ApiError;
		yield* put(showErrorModal(error.message));
	}
};

export const fetchAllLeaderboardsDataSaga = function* ({
	payload,
}: ReturnType<typeof fetchAllLeaderboardsData>): SagaIterator {
	try {
		const selectedLeaderboard = yield* select(getSelectedLeaderboard);
		if (selectedLeaderboard === LeaderboardsType.Yesterday) {
			yield* put(fetchYesterdayLeaderboard(payload));
		}

		if (selectedLeaderboard === LeaderboardsType.Daily) {
			yield* put(fetchDailyLeaderboard(payload));
		}

		if (selectedLeaderboard === LeaderboardsType.AllTime) {
			yield* put(fetchAllTimeLeaderboard(payload));
		}

		// yield* put(fetchAllLeaderboardsDataSuccess());
	} catch (e) {
		yield* put(fetchAllLeaderboardsDataFailed());
	}
};

export const fetchLeaderProfileSaga = function* ({payload}: ReturnType<typeof fetchLeaderProfile>) {
	try {
		const response = yield* call(Api.Ranking.userProfile, payload);
		ApiError.CHECK(response.data);

		yield* put(fetchLeaderProfileSuccess(response.data.success.gameProfile));
	} catch (e) {
		// const user = yield* select(getSentryUser);
		// captureSentryError("fetchLeaderProfileSaga", "none", e, user);
		// yield* put(globalError(e));
		yield* put(fetchLeaderProfileFailed());
	}
};
